import { useMemo } from 'react';
import { useTileData } from '../../hooks/useTileData';
import { suggestAccountOwnersBatch } from '../../api/hub';

/**
 * The suggested account owner for each of a page's unassigned clients, from ONE batched
 * read. A failure contributes nothing — the rows keep their plain "Unassigned" label — so
 * this is an enhancement that can never block or error the list.
 *
 * @param {string[]} clientIds  The unassigned clients currently on screen.
 * @param {boolean} [enabled]   canSuggestOwnersInList(); false makes no call at all.
 * @returns {{suggestions: Map<string, object>, refresh: () => void, error: *}}
 *   `suggestions` maps client id to its best-scored suggestion.
 */
export function useOwnerSuggestions(clientIds, enabled = true) {
  const ids = [...new Set((clientIds || []).filter(Boolean))];
  const key = ids.join(',');
  const active = !!enabled && ids.length > 0;

  const result = useTileData(() => suggestAccountOwnersBatch(ids), [key, active], active);

  const suggestions = useMemo(() => {
    const best = new Map();
    for (const row of result.data?.data || []) {
      const id = row?.client_id;
      if (id == null) continue;
      const current = best.get(id);
      if (!current || (Number(row.score) || 0) > (Number(current.score) || 0)) best.set(id, row);
    }
    return best;
  }, [result.data]);

  return { suggestions, refresh: result.refresh, error: result.error };
}
